/**
 * TaskStatusSelector Component
 *
 * This component renders a combobox for selecting task statuses.
 *
 * Props:
 * - selected (object): The currently selected status.
 * - onChange (function): Callback function to handle changes in selection.
 * - step (boolean): If true, includes the "Skipped" status option to account for Cadence Steps.
 */

import { AdvancedCombobox } from '@evertrue/et-components';
import PropTypes from 'prop-types';

const TaskStatusSelector = ({ value = [], onChange = () => {}, step = false, noneLabel = 'None' }) => {
  const taskStatusOptions = [
    { label: 'Open', value: ['PAUSED', 'IN_PROGRESS'] },
    { label: 'Completed', value: ['COMPLETED'] },
    { label: 'Deleted', value: ['DELETED'] },
    { label: 'Skipped', value: ['SKIPPED_MOVED_JOURNEY', 'SKIPPED_REMOVED_FROM_JOURNEY', 'SKIPPED_MANUAL'] },
    { label: noneLabel, value: ['NONE'] },
  ];

  const isSameValue = (val1, val2) =>
    Array.isArray(val1) && Array.isArray(val2) && val1.length === val2.length && val1.every((v, i) => v === val2[i]);

  const getOptionByValue = (targetValue) => taskStatusOptions.find((opt) => isSameValue(opt.value, targetValue));

  /** Normalize both versions of inputs */
  const normalizeValue = (val) => {
    if (!val?.length) return [];

    // version 2: flat array of strings
    if (typeof val[0] === 'string') {
      const matchedGroups = taskStatusOptions.filter((opt) => opt.value.some((v) => val.includes(v)));
      return matchedGroups;
    }

    // version 1: already in label/value format
    if (val[0]?.value) return val;

    return [];
  };

  const normalizedValue = normalizeValue(value);

  const handleChange = (selectedValues) => {
    const newSelections = selectedValues.filter(
      (item) => !normalizedValue.some((v) => isSameValue(v.value, item.value))
    );
    const noneOption = taskStatusOptions.find((opt) => isSameValue(opt.value, ['NONE']));
    const hasNoneCurrently = normalizedValue.some((opt) => isSameValue(opt.value, ['NONE']));

    if (hasNoneCurrently && newSelections.length > 0) {
      onChange(selectedValues.filter((opt) => !isSameValue(opt.value, ['NONE'])));
      return;
    }

    const isAddingNone = newSelections.some((opt) => isSameValue(opt.value, ['NONE']));
    if (isAddingNone) {
      onChange([noneOption]); // Only "None"
    } else {
      onChange(selectedValues);
    }
  };

  return (
    <div className="task-status-selector--box-wrapper">
      <AdvancedCombobox
        options={taskStatusOptions}
        value={normalizedValue.map((val) => getOptionByValue(val.value)).filter(Boolean)}
        search_placeholder={step ? 'Search Step Status' : 'Search Task Status'}
        placeholder="Select Status..."
        onChange={handleChange}
        searchable
        multiple
        allowChangeAll
        allowUnselectAll
      />
    </div>
  );
};

TaskStatusSelector.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  step: PropTypes.bool,
  noneLabel: PropTypes.string,
};

export default TaskStatusSelector;
